import { useMemo } from 'react';
import { CATALOGUE, getActiveSources, isRenderable } from '../data/catalogue';
import { mapCredits } from '../data/attributions';

/**
 * The attribution lines owed for whatever the map is currently rendering.
 *
 * Separate from useLegend on purpose. The legend answers "what do these colours
 * mean", which is a farmer-facing question and is allowed to merge, dedupe and
 * shorten. This answers "whose data is on screen", which is a licence
 * obligation and must follow the sources actually drawn, including the ones
 * that contribute no legend card of their own. Merging the two would make one
 * of them wrong.
 *
 * @param {object} layerStates the category visibility map
 * @param {string|null} viewportRegion 'england' | 'scotland' | 'wales' | null
 * @returns {Array<{text: string, url?: string}>} credit lines, deduplicated,
 *   stable order. `url` is present only where the publisher points at a fuller
 *   attribution document than the line itself carries.
 */
export function useMapCredits(layerStates, viewportRegion) {
  return useMemo(() => {
    const region =
      viewportRegion === 'england' ||
      viewportRegion === 'scotland' ||
      viewportRegion === 'wales'
        ? viewportRegion
        : null;

    const activeSourceIds = [];
    for (const category of CATALOGUE) {
      if (!layerStates?.[category.id]?.visible) continue;
      if (!region) continue;
      for (const source of getActiveSources(category, region)) {
        // isRenderable, so a source the app declines to draw is not credited
        // for being drawn.
        if (isRenderable(source)) activeSourceIds.push(source.id);
      }
    }

    return mapCredits(activeSourceIds);
  }, [layerStates, viewportRegion]);
}
